/**
 * Terminal wire protocol.
 *
 * This is a separate WebSocket from the domain-event channel and carries binary
 * frames (ADR-0004). Base64-in-JSON would inflate every byte of PTY output by a
 * third and add a parse on each frame, on the one channel where latency is
 * actually perceptible.
 *
 * Frame layout, big-endian:
 *
 *   byte 0        frame type
 *   bytes 1..n    type-specific header
 *   remainder     payload
 *
 * A one-byte discriminator is enough — there are seven frame types and no
 * prospect of hundreds.
 */

export const enum FrameType {
  /** Server → client: raw PTY bytes. */
  Output = 0x01,
  /** Client → server: keystrokes, carrying a sequence for exactly-once. */
  Input = 0x02,
  /** Client → server: the viewport changed size. */
  Resize = 0x03,
  /** Server → client: input with this sequence was written to the PTY. */
  Ack = 0x04,
  /** Server → client: session state changed (JSON payload). */
  Status = 0x05,
  /** Client → server: request or release the input control lease. */
  Control = 0x06,
  /** Bidirectional keepalive. */
  Ping = 0x07,
  /**
   * Server → client: input with this sequence was NOT applied, and the server
   * wants `expected` next.
   *
   * Without it a refused frame was answered with silence, so this client's
   * counter and the server's cursor could never re-converge: one keystroke
   * typed during a momentary loss of control left the pane mute until the page
   * was reloaded.
   */
  Reject = 0x08,
}

export interface OutputFrame {
  type: FrameType.Output
  data: Uint8Array
  /** Monotonic offset into the session's output stream. Used as the reconnect
   *  cursor, so a returning client replays only what it missed. */
  offset: number
}

export interface InputFrame {
  type: FrameType.Input
  clientId: string
  sequence: number
  data: Uint8Array
}

export interface ResizeFrame {
  type: FrameType.Resize
  cols: number
  rows: number
}

export interface AckFrame {
  type: FrameType.Ack
  sequence: number
}

export interface StatusFrame {
  type: FrameType.Status
  status: TerminalStatus
}

export interface RejectFrame {
  type: FrameType.Reject
  /** The sequence that was refused. */
  sequence: number
  /** The sequence the server will accept next. */
  expected: number
}

export interface ControlFrame {
  type: FrameType.Control
  action: 'acquire' | 'release' | 'heartbeat'
}

export type Frame = OutputFrame | InputFrame | ResizeFrame | AckFrame | StatusFrame | ControlFrame

export interface TerminalStatus {
  sessionId: string
  kind: 'MANAGED' | 'ADOPTED' | 'OBSERVED'
  state: string
  /** Who currently holds the input lease; null when nobody does. */
  controller: string | null
  /** Whether this client is the controller. */
  youAreController: boolean
  /**
   * Whether an acquire from *this* connection would be granted.
   *
   * Not derivable from `controller`, which is why the server sends it. The pane
   * used to infer it — "somebody holds the lease, so the control is dead" — and
   * that is wrong in the one case the server bends over backwards to allow: this
   * operator's own second connection taking their keyboard back. It was also
   * wrong the other way on a LOCKED session, where the control stayed live and
   * pressing it did nothing.
   *
   * Optional so a pane connected to a server that predates the field falls back
   * to the old inference rather than to a dead button.
   */
  canTakeControl?: boolean
  fencingToken?: number
  recording: boolean
  /**
   * Whether a live PTY client stands between the session and its tmux session.
   *
   * False means the work is still running but nothing is reading it: no output
   * will arrive and no keystroke will land. The node retries the attachment on
   * its next sweep; until then the pane has to say so rather than present a
   * frozen terminal as a working one.
   */
  attached?: boolean
  /**
   * The agent saying, in its own terminal title, that it is at work.
   *
   * Absent is not "idle": a pane running a shell has no such signal to give, and
   * that one is judged by its output as before. It exists because output cannot
   * answer for an agent waiting on a tool — it prints nothing for as long as the
   * tool runs, and by bytes alone that is indistinguishable from having stopped.
   */
  workingNow?: boolean
  cols: number
  rows: number
  cwd?: string
  /**
   * The output that follows is not a continuation of what is on screen.
   *
   * Terminal output is a stateful stream: a full-screen program sends
   * differences against the screen it believes is there. When the server cannot
   * resume a client where it left off — most often because the control plane
   * restarted and the session was re-adopted with a fresh scrollback ring while
   * this tab kept its old cursor — those differences land on a screen nobody is
   * describing any more, and the pane fills with the old text and the new text
   * interleaved character by character. Clearing first is what makes the replay
   * render as a screen instead of as a correction to one.
   *
   * Sent once, on the first status frame of a connection.
   */
  streamReset?: boolean
}

const encoder = new TextEncoder()
const decoder = new TextDecoder()

/* ------------------------------------------------------------- Encoding */

/**
 * Encodes an input frame.
 *
 * The sequence travels with every keystroke because the node deduplicates on
 * (clientId, sequence): a reconnect that resends unacknowledged input must not
 * type it into the PTY twice. Typing a command twice into a shell is not a
 * cosmetic bug.
 */
export function encodeInput(clientId: string, sequence: number, data: string): ArrayBuffer {
  const idBytes = encoder.encode(clientId)
  const payload = encoder.encode(data)

  // 1 type + 1 idLen + id + 4 sequence + payload
  const buf = new ArrayBuffer(1 + 1 + idBytes.length + 4 + payload.length)
  const view = new DataView(buf)
  const bytes = new Uint8Array(buf)

  let off = 0
  view.setUint8(off++, FrameType.Input)
  view.setUint8(off++, idBytes.length)
  bytes.set(idBytes, off)
  off += idBytes.length
  view.setUint32(off, sequence, false)
  off += 4
  bytes.set(payload, off)

  return buf
}

/**
 * Encodes a viewport claim: what this pane can display, and whether it is the
 * whole view or one tile on a wall.
 *
 * The rank is a sixth byte, appended rather than folded into a spare bit of the
 * five that were already there, so that neither skew needs a negotiation: a
 * server that predates it reads the first five bytes and ignores the rest, and
 * one that knows it reads a five-byte frame as the ambient claim every claim
 * used to be. It is written only when set, so an ordinary tile sends exactly the
 * bytes this function has always sent.
 */
export function encodeResize(cols: number, rows: number, primary = false): ArrayBuffer {
  const buf = new ArrayBuffer(primary ? 6 : 5)
  const view = new DataView(buf)
  view.setUint8(0, FrameType.Resize)
  view.setUint16(1, cols, false)
  view.setUint16(3, rows, false)
  if (primary) view.setUint8(5, 1)
  return buf
}

export function encodeControl(action: ControlFrame['action']): ArrayBuffer {
  const payload = encoder.encode(action)
  const buf = new ArrayBuffer(1 + payload.length)
  new Uint8Array(buf).set(payload, 1)
  new DataView(buf).setUint8(0, FrameType.Control)
  return buf
}

export function encodePing(): ArrayBuffer {
  const buf = new ArrayBuffer(1)
  new DataView(buf).setUint8(0, FrameType.Ping)
  return buf
}

/* ------------------------------------------------------------- Decoding */

export type DecodedFrame =
  | OutputFrame
  | AckFrame
  | StatusFrame
  | RejectFrame
  | { type: FrameType.Ping }
  | { type: 'unknown'; raw: number }

/**
 * Decodes a server frame.
 *
 * A frame that is truncated or carries an unrecognised type is reported rather
 * than thrown: a single bad frame must not tear down a working terminal, and a
 * newer server sending a frame type this client does not know is a version skew
 * to survive, not to crash on.
 */
export function decodeFrame(buf: ArrayBuffer): DecodedFrame {
  if (buf.byteLength < 1) return { type: 'unknown', raw: -1 }

  const view = new DataView(buf)
  const kind = view.getUint8(0)

  switch (kind) {
    case FrameType.Output: {
      if (buf.byteLength < 9) return { type: 'unknown', raw: kind }
      // A 64-bit offset because a long-lived session can emit more than 4 GiB.
      const offset = Number(view.getBigUint64(1, false))
      return { type: FrameType.Output, offset, data: new Uint8Array(buf, 9) }
    }

    case FrameType.Ack: {
      if (buf.byteLength < 5) return { type: 'unknown', raw: kind }
      return { type: FrameType.Ack, sequence: view.getUint32(1, false) }
    }

    case FrameType.Status: {
      try {
        const json = decoder.decode(new Uint8Array(buf, 1))
        return { type: FrameType.Status, status: JSON.parse(json) as TerminalStatus }
      } catch {
        return { type: 'unknown', raw: kind }
      }
    }

    case FrameType.Reject: {
      if (buf.byteLength < 9) return { type: 'unknown', raw: kind }
      return {
        type: FrameType.Reject,
        sequence: view.getUint32(1, false),
        expected: view.getUint32(5, false),
      }
    }

    case FrameType.Ping:
      return { type: FrameType.Ping }

    default:
      return { type: 'unknown', raw: kind }
  }
}

/* ---------------------------------------------------------------- Resync */

/**
 * Works out how to resume after the server refused a frame.
 *
 * The server says which sequence it wants next. Everything the client still
 * holds from the refused sequence onwards has to be renumbered from there and
 * resent, in order; anything below it the server has already applied and must
 * not receive again.
 *
 * Pure, and separate from the pane, because the interesting part is arithmetic
 * on an exactly-once cursor and that deserves a test that needs no socket.
 */
export function resyncPending(
  pending: Map<number, string>,
  sequence: number,
  expected: number,
): { nextSequence: number; replay: string[] } {
  const replay = [...pending.entries()]
    .sort((a, b) => a[0] - b[0])
    .filter(([seq]) => seq >= sequence)
    .map(([, data]) => data)
  // The counter is set to the sequence *before* the resume point, because the
  // sender pre-increments.
  return { nextSequence: Math.max(0, expected - 1), replay }
}

/* ------------------------------------------------------- Output hygiene */

/**
 * Neutralises terminal escape sequences that a hostile process could use to
 * attack the operator's browser or clipboard.
 *
 * The threat is concrete: a compromised repository can make a build tool print
 * an OSC 52 sequence that writes to the system clipboard, or an OSC 8 hyperlink
 * pointing somewhere other than its visible text. xterm.js renders these
 * faithfully, which is correct behaviour for a terminal emulator and wrong for
 * one embedded in a browser session that also holds an authenticated cookie.
 *
 * The node sanitises as well; this is the second layer, because the browser is
 * where the consequences land.
 *
 * What is stripped:
 *   OSC 52  — clipboard read/write
 *   OSC 0/1/2 — window and icon title (used to smuggle text into the tab title)
 *   OSC 7   — cwd reporting, which some terminals act on
 *   OSC 777 — desktop notifications
 *   DCS/APC/PM/SOS — device-control and application strings, which have no
 *                    legitimate use in this context
 *
 * OSC 8 hyperlinks are deliberately preserved: they are genuinely useful, and
 * the link handler requires explicit confirmation before opening anything.
 */
export function sanitizeOutput(text: string): string {
  return (
    text
      // OSC sequences with a numeric command, terminated by BEL or ST.
      .replace(/\](0|1|2|7|52|777)(;[^]*)?(|\\)/g, '')
      // DCS ... ST
      .replace(/P[\s\S]*?\\/g, '')
      // APC / PM / SOS ... ST
      .replace(/[_^X][\s\S]*?\\/g, '')
  )
}

/**
 * The server's per-frame payload cap, in UTF-8 bytes.
 *
 * Must match MaxInputPayloadBytes in internal/api/terminal_protocol.go. A frame
 * over it is a *fatal* decode error there, not a refusal: the socket is closed.
 */
export const MAX_INPUT_PAYLOAD_BYTES = 64 << 10

/**
 * Splits input into frames the server will accept.
 *
 * A paste larger than the cap used to be sent whole, which closed the socket —
 * and because the frame was already in the pending buffer it was replayed on
 * every reconnect, so one large paste flapped the terminal permanently. Chunking
 * keeps exactly-once intact: each chunk takes its own sequence and the order is
 * preserved.
 *
 * Budgeted in UTF-8 bytes and split on code-point boundaries, because the wire
 * limit counts bytes while a string counts units — a naive slice would cut a
 * surrogate pair in half and put a replacement character into the operator's
 * command.
 */
export function chunkInput(text: string, maxBytes = MAX_INPUT_PAYLOAD_BYTES): string[] {
  if (encoder.encode(text).length <= maxBytes) return text.length > 0 ? [text] : []

  const chunks: string[] = []
  let current = ''
  let bytes = 0
  // Iterating the string yields whole code points, so a surrogate pair is never
  // split across two chunks.
  for (const ch of text) {
    const size = encoder.encode(ch).length
    if (bytes + size > maxBytes && current !== '') {
      chunks.push(current)
      current = ''
      bytes = 0
    }
    current += ch
    bytes += size
  }
  if (current !== '') chunks.push(current)
  return chunks
}

/**
 * Decides whether a pasted payload is large enough to warrant confirmation.
 *
 * Pasting a multi-line block into a shell executes every line but the last
 * immediately. An operator who meant to paste a filename and pasted a script
 * has no undo, so anything multi-line or long asks first.
 */
export function shouldConfirmPaste(text: string): { confirm: boolean; lines: number } {
  const lines = text.split('\n').length
  return { confirm: lines > 1 || text.length > 512, lines }
}
